<script lang="ts" setup>
import { Head, usePage } from "@inertiajs/vue3";
import HeadingComponent from "../../Components/HeadingComponent.vue";
import EventPublicWrapper from "../EventPublic/EventPublicWrapper.vue";
import "/node_modules/flag-icons/css/flag-icons.min.css";
import { toEventLocalISOString } from "@/Helpers/helperFunctions";

interface Props {}

const props = withDefaults(defineProps<Props>(), {});

const denied_until = usePage().props.scheduled_task
    ? new Date(
          toEventLocalISOString(usePage().props.scheduled_task.scheduled_at, usePage().props.event.timezone),
      ).toLocaleString()
    : null;
</script>

<template>
    <EventPublicWrapper>
        <Head>
            <title>Event {{ $page.props.page_name }}: {{ $page.props.event.name }}</title>
            <meta
                property="og:title"
                head-key="og:title"
                :content="`Event ${$page.props.page_name}: ${$page.props.meta_event_name}`"
            />
            <meta
                name="robots"
                content="noindex, nofollow"
            />
        </Head>
        <HeadingComponent class="rounded-lg bg-white p-4 shadow-lg sm:px-6">
            <template #name>{{ $page.props.page_name }}</template>
            <template #description>
                <div
                    v-if="$page.props.competitor"
                    class="mb-4 rounded-lg border border-green-300 bg-green-50 p-4 text-green-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-sm font-medium">
                            <strong>{{ $page.props.competitor.driver_name }}</strong
                            >, your status is currently: <strong>{{ $page.props.competitor.status }}</strong>
                        </h3>
                    </div>

                    <div>
                        <div class="mt-2 text-sm">
                            You have successfully registered for this event. <br />
                            Check you email and RBR Roadbook Reader app for the next steps! <br />
                            <br />
                            Learn more:
                            <a href="/faq/how-to-participate-in-the-event-25"
                                >How to participate in the event?</a
                            >
                        </div>
                    </div>
                </div>

                <div
                    v-if="$page.props.scheduled_task"
                    class="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-sm font-medium">This page will be accessible starting from:</h3>
                    </div>

                    <div>
                        <div class="mt-2 text-sm">
                            {{ denied_until }}
                        </div>
                    </div>
                </div>
                <div
                    :class="
                        $page.props.visibility === 'participants'
                            ? 'mb-4 flex rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800'
                            : 'mb-4 flex rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800'
                    "
                    role="alert"
                >
                    <svg
                        class="me-3 mt-[2px] inline size-4 shrink-0"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                        />
                    </svg>
                    <div v-if="$page.props.visibility === 'participants'">
                        <span class="font-medium"
                            >Restricted access
                            {{ $page.props.scheduled_task ? `(until ${denied_until})` : "" }}</span
                        >
                        <div class="mt-2 text-sm">
                            <div>
                                Only event competitors with the status "accepted" or "tester" are permitted to
                                access this page.
                            </div>
                            <div v-if="!$page.props.user">
                                <br />
                                If you are a competitor or an organizing team member of this event, please log
                                in to view this page.
                            </div>
                        </div>
                    </div>
                    <div v-if="$page.props.visibility === 'hidden'">
                        <span class="font-medium"
                            >Access denied
                            {{ $page.props.scheduled_task ? `(until ${denied_until})` : "" }}</span
                        >
                        <div class="mt-2 text-sm">
                            <div>
                                The event organizer has restricted access to this page. Only members of the
                                organizing team are allowed access.
                            </div>
                            <div v-if="!$page.props.user">
                                <br />
                                If you are a member of the organizing team, please log in to view this page.
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="$page.props.comment"
                    class="mb-4 rounded-lg border border-blue-300 bg-blue-50 p-4 text-blue-800"
                    role="alert"
                >
                    <div class="flex items-center">
                        <svg
                            class="me-2 size-4 shrink-0"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                            />
                        </svg>
                        <span class="sr-only">Info</span>
                        <h3 class="text-sm font-medium">Comment from the event organizer:</h3>
                    </div>

                    <div>
                        <div class="mt-2 text-sm">
                            {{ $page.props.comment }}
                        </div>
                    </div>
                </div>
            </template>
        </HeadingComponent>
    </EventPublicWrapper>
</template>
